import type { AccessControlService } from "@dis/auth";
import admin from "../../routes/admin.routes";
import billing from "../../routes/billing.routes";
import dashboards from "../../routes/dashboards.routes";
import dev from "../../routes/dev.routes";
import engineering from "../../routes/engineering.routes";
import map from "../../routes/map.routes";
import operations from "../../routes/operations.routes";
import projects from "../../routes/projects.routes";
import purchasing from "../../routes/purchasing.routes";
import quality from "../../routes/quality.routes";
import sales from "../../routes/sales.routes";
import shop from "../../routes/shop.routes";
import {
  JtmsRoutes,
  NAV_GROUP_ICONS,
  NAV_GROUP_LABELS,
  NAV_GROUP_ORDER,
  NavGroup,
  RouteScope,
} from "./route-data";

// Each group's routes, mounted in app.routes.ts at /{group}. The nav reads them directly, so a page shows up in the
// nav by having a navOrder and a title; routes.spec.ts checks every entry still resolves.
export const NAV_GROUP_ROUTES: Record<NavGroup, JtmsRoutes> = {
  dashboards,
  map,
  projects,
  sales,
  engineering,
  operations,
  shop,
  quality,
  purchasing,
  billing,
  admin,
  dev,
};

export type NavEntry = {
  title: string;
  link: string[];
  order: number;
  // Report pages only: the groups whose reports list includes it, owner first.
  reportGroups?: NavGroup[];
};

export type NavGroupEntries = {
  group: NavGroup;
  label: string;
  icon: string;
  entries: NavEntry[];
};

export type NavAccess = Pick<AccessControlService, "can" | "canAnywhere" | "isSystemUser">;

// Pages with no scopes of their own need some read grant, at any site.
const DEFAULT_SCOPES: readonly RouteScope[] = [{ domain: "ALL", level: "VIEWER", target: "anywhere" }];

const allowed = (scopes: readonly RouteScope[], adminOnly: boolean, access: NavAccess): boolean => {
  if (access.isSystemUser()) return true;
  if (adminOnly) return false;
  return (scopes.length ? scopes : DEFAULT_SCOPES).every(({ domain, level, target }) =>
    target === "anywhere" ? access.canAnywhere(domain, level) : access.can(domain, level, target)
  );
};

/**
 * A group's nav entries the user may see, in navOrder. Param, matcher, redirect, outlet and lazy-children routes are
 * skipped: the nav only links to static pages declared in the group file.
 */
export function navEntries(group: NavGroup, access: NavAccess): NavEntry[] {
  const entries: NavEntry[] = [];
  const walk = (routes: JtmsRoutes, segments: string[], scopes: readonly RouteScope[], adminOnly: boolean) => {
    for (const route of routes) {
      if (route.matcher || route.redirectTo !== undefined || route.outlet) continue;
      const path = route.path ?? "";
      if (path.includes(":") || path.includes("*")) continue;

      const routeSegments = [...segments, ...path.split("/").filter(Boolean)];
      const routeScopes = [...scopes, ...(route.data?.scopes ?? [])];
      const routeAdminOnly = adminOnly || !!route.data?.adminOnly;
      const order = route.data?.navOrder;

      if (order !== undefined && typeof route.title === "string" && allowed(routeScopes, routeAdminOnly, access)) {
        const { report, reportOwner, alsoIn = [] } = route.data ?? {};
        entries.push({
          title: route.title,
          link: ["/", group, ...routeSegments],
          order,
          ...(report && { reportGroups: [reportOwner ?? group, ...alsoIn] }),
        });
      }
      if (route.children) walk(route.children, routeSegments, routeScopes, routeAdminOnly);
    }
  };
  walk(NAV_GROUP_ROUTES[group], [], [], false);
  return entries.sort((a, b) => a.order - b.order);
}

/** Every group the user can use, in NAV_GROUP_ORDER: a placeholder Overview alone doesn't count. */
export function navGroups(access: NavAccess): NavGroupEntries[] {
  return NAV_GROUP_ORDER.map((group) => ({
    group,
    label: NAV_GROUP_LABELS[group],
    icon: NAV_GROUP_ICONS[group],
    entries: navEntries(group, access),
  })).filter((group) => group.entries.some((entry) => entry.link.length > 2 || entry.title !== "Overview"));
}

export type ReportGroup = { group: NavGroup; label: string; reports: NavEntry[] };

/** Every report the user can open, listed under its owning group and any alsoIn groups, in NAV_GROUP_ORDER. */
export function navReports(access: NavAccess): ReportGroup[] {
  const reports = NAV_GROUP_ORDER.flatMap((group) => navEntries(group, access)).filter((entry) => entry.reportGroups);
  return NAV_GROUP_ORDER.map((group) => ({
    group,
    label: NAV_GROUP_LABELS[group],
    reports: reports.filter((entry) => entry.reportGroups?.includes(group)),
  })).filter((group) => group.reports.length);
}
